import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Trash2, CheckCircle2, XCircle, FileStack, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import BeforeAfter from "@/components/BeforeAfter";
import Lightbox from "@/components/Lightbox";

const fmt = (s) => (s ? new Date(s).toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short" }) : "");

function RequestCard({ d, onDone, onOpen }) {
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState("");
  const decide = async (approve) => {
    setBusy(true);
    try {
      await api.post(`/defects/${d.id}/deletion-request/decide`, { approve, reason });
      toast.success(approve ? `${d.number} als erledigt archiviert` : `Anfrage zu ${d.number} abgelehnt`); onDone(d.id); window.dispatchEvent(new Event("pd-deletion-requests-changed"));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setBusy(false); }
  };
  const r = d.deletion_request;
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 grid md:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)] gap-4 fade-up" data-testid={`dr-card-${d.id}`}>
      <div className="min-w-0 space-y-2">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-mono-tech text-[10px] font-bold text-red-600 bg-red-50 px-1.5 py-0.5 rounded">{d.number}</span>
          <span className="font-semibold text-slate-900">{d.bezeichnung}</span>
        </div>
        <div className="text-xs text-slate-500">{[d.project_name, d.position_number && `Position ${d.position_number}`, d.room].filter(Boolean).join(" · ")}</div>
        {d.description && <p className="text-sm text-slate-600">{d.description}</p>}
        {d.actual_measure && <p className="text-xs text-slate-500"><b>Durchgeführt:</b> {d.actual_measure}</p>}
        <div className="text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-2.5 py-1.5">
          Angefragt von <b>{r.requested_by_name}</b> am {fmt(r.requested_at)}{r.note ? ` – „${r.note}“` : ""}
        </div>
        {d.plan_id && d.position_id && (
          <Link to={`/plans/${d.plan_id}?position=${d.position_id}&tab=maengel`} className="text-xs text-primary font-medium inline-flex items-center gap-1" data-testid={`dr-open-plan-${d.id}`}>
            <FileStack className="h-3.5 w-3.5" />Im Bauplan öffnen</Link>
        )}
        <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Begründung bei Ablehnung (optional)" className="h-8 text-sm" data-testid={`dr-reason-${d.id}`} />
        <div className="flex gap-2">
          <Button size="sm" className="bg-green-600 hover:bg-green-700" disabled={busy} onClick={() => decide(true)} data-testid={`dr-approve-${d.id}`}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CheckCircle2 className="h-4 w-4 mr-1" />Bestätigen & archivieren</>}</Button>
          <Button size="sm" variant="outline" disabled={busy} onClick={() => decide(false)} data-testid={`dr-reject-${d.id}`}><XCircle className="h-4 w-4 mr-1" />Ablehnen</Button>
        </div>
      </div>
      <BeforeAfter before={d.before_photo} after={d.after_photo} onOpen={onOpen} testid={`dr-photos-${d.id}`} />
    </div>
  );
}

export default function DeletionRequests() {
  const [items, setItems] = useState(null);
  const [error, setError] = useState("");
  const [lightbox, setLightbox] = useState(null);
  useEffect(() => {
    api.get("/defects/deletion-requests").then((r) => setItems(r.data)).catch((e) => setError(formatApiError(e.response?.data?.detail)));
  }, []);
  return (
    <div className="space-y-5" data-testid="deletion-requests-page">
      <div>
        <h1 className="text-3xl sm:text-4xl font-head font-extrabold tracking-tight text-slate-900 flex items-center gap-2"><Trash2 className="h-8 w-8 text-primary" />Löschanfragen</h1>
        <p className="text-slate-500 mt-1">Von der Haustechnik als behoben gemeldete Mängel. Bestätigen archiviert den Mangel als erledigt (automatische Löschung nach 6 Wochen).</p>
      </div>
      {error && <div className="text-sm text-red-600" data-testid="dr-error">{error}</div>}
      {!items && !error && <div className="space-y-3">{[0, 1].map((i) => <div key={i} className="h-44 bg-slate-100 rounded-xl animate-pulse" />)}</div>}
      {items && items.length === 0 && <div className="bg-white border border-slate-200 rounded-xl p-10 text-center text-sm text-slate-400" data-testid="dr-empty">Keine offenen Löschanfragen.</div>}
      {items && items.length > 0 && <div className="text-sm text-slate-600" data-testid="dr-count">{items.length} offene Anfrage{items.length === 1 ? "" : "n"}</div>}
      <div className="space-y-3">
        {items?.map((d) => <RequestCard key={d.id} d={d} onOpen={(id) => setLightbox(id)} onDone={(id) => setItems((xs) => xs.filter((x) => x.id !== id))} />)}
      </div>
      {lightbox && <Lightbox fileId={lightbox} onClose={() => setLightbox(null)} />}
    </div>
  );
}
